import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Paper, Text } from '@mantine/core';
import { Calendar, TimeInput } from '@mantine/dates';
import { addDays, format } from 'date-fns';

import { useGetForm, usePatchForm } from 'api/forms';
import { Form } from 'components/shared';
import { ClockIcon } from 'icons';

import { useFormEditContext } from '../../context';
import { useShowNotification } from '../../../../../hooks';

type Props = {
  opened: boolean;
  onClose: () => void;
};

type FormData = {
  expirationDate: Date;
  expirationTime: Date;
};

export const ExpirationModal = ({ opened, onClose }: Props) => {
  const showNotification = useShowNotification();
  const {
    form: { id },
  } = useFormEditContext();
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      expirationDate: addDays(new Date(), 1),
      expirationTime: new Date(),
    },
  });
  const [expirationDateValue, expirationTimeValue] = watch([
    'expirationDate',
    'expirationTime',
  ]);
  const { mutateAsync: patchForm } = usePatchForm({ id });
  const { refetch: refetchForm } = useGetForm({ id });
  const { t } = useTranslation('forms');

  const onSubmit = async ({ expirationDate, expirationTime }: FormData) => {
    const dateString = format(expirationDate, 'yyyy-MM-dd');
    const timeString = format(expirationTime, 'HH:mm');
    await patchForm({
      autoDeactivateAt: `${dateString}T${timeString}:00.000Z`,
    });
    await refetchForm();
    onClose();
  };

  const onSuccess = () => {
    showNotification({
      message: t('editView.sidebar.sharing.settings.expiration.successToast'),
      color: 'green',
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('editView.sidebar.sharing.expirationModal.title')}
      size="md"
      centered
    >
      <Form onSubmit={handleSubmit(onSubmit)} onSuccess={onSuccess}>
        <Flex direction="column" gap="md">
          <Text size="md">
            {t('editView.sidebar.sharing.expirationModal.description')}
          </Text>
          <Paper sx={{ display: 'flex', justifyContent: 'center' }}>
            <Calendar
              value={expirationDateValue}
              onChange={value => {
                if (value) setValue('expirationDate', value);
              }}
              minDate={new Date()}
            />
          </Paper>
          <TimeInput
            value={expirationTimeValue}
            onChange={value => {
              setValue('expirationTime', value);
            }}
            label={t(
              'editView.sidebar.sharing.expirationModal.timeField.label',
            )}
            icon={<ClockIcon />}
          />
          <Flex justify="flex-end" gap="md">
            <Button variant="default" onClick={onClose} disabled={isSubmitting}>
              {t('editView.sidebar.sharing.expirationModal.cancelButton')}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {t('editView.sidebar.sharing.expirationModal.saveButton')}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  );
};
